import { CheckCircle, Warning } from '@phosphor-icons/react'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'

import { Button, Modal } from 'DesignSystem'
import LoadingDots from 'DesignSystem/LoadingDots'
import useIntlShort from 'Hooks/useIntlShort.hook'
import useOAuthGrants from 'Hooks/API/useOAuthGrants.hook'
import type { OAuthGrantI } from 'Hooks/API/useOAuthGrants.hook'
import { apiDELETE } from 'Services/Api.service'
import { addAlert } from 'Store/Actions/alerts.action'

import { RootS, ListS, AppS, EmptyS } from './ConnectedApps.styled'

/**
 * Aplicativos aos quais o usuário concedeu acesso via OAuth, e onde ele corta
 * esse acesso.
 *
 * Desconectar revoga o consentimento e os refresh tokens no user-api — o
 * aplicativo precisa pedir autorização de novo para voltar a funcionar.
 */

const ConnectedAppsPage = () => {
  const intl = useIntlShort()
  const theme = useTheme()
  const dispatch = useDispatch()

  const { data: grants, isLoading, mutate } = useOAuthGrants()

  const [toDisconnect, setToDisconnect] = useState<OAuthGrantI | null>(null)
  const [disconnecting, setDisconnecting] = useState(false)

  const disconnect = async () => {
    if (!toDisconnect) return

    setDisconnecting(true)

    try {
      const response = await apiDELETE<any>(`oauth/grants/${toDisconnect.id}`)

      // 4xx não lança do axios (validateStatus: status < 500)
      if (response?.status === 'error') {
        dispatch(addAlert({ title: response.message ?? intl('Não foi possível desconectar o aplicativo.'), type: 'error' }))
      } else {
        dispatch(addAlert({ title: intl('Aplicativo desconectado.'), type: 'success' }))
        mutate()
      }
    } catch (error: any) {
      dispatch(addAlert({ title: error?.message ?? intl('Não foi possível desconectar o aplicativo.'), type: 'error' }))
    }

    setDisconnecting(false)
    setToDisconnect(null)
  }

  return (
    <RootS>
      <h3 className="fw-bold">{intl('Aplicativos conectados')}</h3>
      <p className="body2 text-gray_hover">
        {intl('Aplicativos que você autorizou a acessar sua conta. Desconectar revoga o acesso imediatamente.')}
      </p>

      {isLoading && <div className="mt-5 d-flex justify-content-center"><LoadingDots /></div>}

      {!isLoading && grants.length === 0 && (
        <EmptyS>
          <h4 className="fw-bold">{intl('Nenhum aplicativo conectado')}</h4>
          <p className="body2 text-gray_hover mt-2">
            {intl('Quando você autorizar um aplicativo a acessar sua conta, ele aparecerá aqui.')}
          </p>
        </EmptyS>
      )}

      {!isLoading && grants.length > 0 && (
        <ListS className="mt-4">
          {grants.map(grant => (
            <AppS key={grant.id}>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span className="body1 fw-bold">
                    {grant.client.client_name ?? intl('Aplicativo')}
                  </span>
                  {grant.client.name_verified
                    ? <CheckCircle weight="fill" size={20} color={theme.colors.success} />
                    : <Warning weight="fill" size={20} color={theme.colors.alert} />}
                </div>

                {!grant.client.name_verified && (
                  <p className="caption1 text-gray_hover mt-1">
                    {intl('Aplicativo não verificado')}
                  </p>
                )}

                <p className="body3 text-gray_hover mt-2">
                  {grant.scopes.join(', ')}
                </p>
              </div>

              <Button color="gray" onClick={() => setToDisconnect(grant)}>
                {intl('Desconectar')}
              </Button>
            </AppS>
          ))}
        </ListS>
      )}

      <Modal isOpen={!!toDisconnect} onClose={() => setToDisconnect(null)}>
        <h4 className="fw-bold">{intl('Desconectar aplicativo?')}</h4>
        <p className="body2 mt-3">
          {intl('{app} perderá o acesso à sua conta imediatamente. Para voltar a usá-lo, será preciso autorizar de novo.', {
            app: toDisconnect?.client.client_name ?? intl('O aplicativo')
          })}
        </p>
        <div className="d-flex flex-row gap-3 mt-4">
          <Button className="flex-grow-1" color="gray" onClick={() => setToDisconnect(null)}>
            {intl('Cancelar')}
          </Button>
          <Button className="flex-grow-1" color="error" isLoading={disconnecting} onClick={disconnect}>
            {intl('Desconectar')}
          </Button>
        </div>
      </Modal>
    </RootS>
  )
}

export default ConnectedAppsPage
